/**
 * Single-Segment Visitor Route: /{mapSlug}
 *
 * On subdomain/custom_domain mode: orgSlug comes from hostname, this is the map slug.
 * On dev/admin mode: single segment = orgSlug, resolve active map and redirect to /{orgSlug}/{mapSlug}.
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import { detectTenant } from "../hooks/use-tenant";
import { validateVisitorSearch } from "../lib/visitor-search";
import { resolveOrgFromHostname } from "../lib/resolve-org";
import { VisitorMapBySlug } from "../components/visitor/VisitorMapBySlug";

export const Route = createFileRoute("/$mapSlug")({
  validateSearch: validateVisitorSearch,

  beforeLoad: async ({ params }) => {
    const tenant = detectTenant();

    // DEV / ADMIN MODE:
    // Single segment = orgSlug, not mapSlug.
    // Redirect to admin dashboard — app.wondermaps.co is staff-only.
    if (tenant.mode === "dev" || tenant.mode === "admin") {
      const orgSlug = params.mapSlug;
      throw redirect({
        to: "/$orgSlug/admin",
        params: { orgSlug },
      });
    }

    // SUBDOMAIN MODE:
    // orgSlug comes from hostname via detectTenant()
    if (tenant.mode === "subdomain") {
      if (!tenant.orgSlug) throw redirect({ to: "/" });
      return { orgSlug: tenant.orgSlug };
    }

    // CUSTOM DOMAIN MODE:
    // orgSlug is null on the client — resolve from hostname via API.
    if (tenant.mode === "custom_domain") {
      const orgSlug = await resolveOrgFromHostname(window.location.hostname);
      return { orgSlug };
    }

    // Fallback — shouldn't reach here
    throw new Error("Unknown tenant mode");
  },
  component: MapSlugRoute,
});

function MapSlugRoute() {
  const { mapSlug } = Route.useParams();
  const search = Route.useSearch();
  const ctx = Route.useRouteContext() as { orgSlug: string };
  return <VisitorMapBySlug orgSlug={ctx.orgSlug} mapSlug={mapSlug} search={search} />;
}
